import { useState } from "react";
import { Loader2, RefreshCw, Unplug, Plug, AlertCircle } from "lucide-react";
import { SettingsPage } from "@/components/v2/settings-page";
import { ConfirmDialog } from "@/components/v2/confirm-dialog";
import { StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  useIntegrations,
  useConnectIntegration,
  useDisconnectIntegration,
  useSyncIntegration,
} from "@/hooks/use-integrations";
import type { IntegrationConnection } from "@adva/types";
// angi.svg is reused for both Angi Leads and Angi Ads — both are products of
// the same Angi brand and share a single mark.
import angiLogo from "@adva/brand-assets/integrations/angi.svg";
import callrailLogo from "@adva/brand-assets/integrations/callrail.svg";
import companycamLogo from "@adva/brand-assets/integrations/companycam.svg";
import gohighlevelLogo from "@adva/brand-assets/integrations/gohighlevel.svg";
// google.svg is reused for Google Business Profile, Google Workspace, and
// Google Local Services Ads — Google publishes no distinct umbrella mark for
// these; only the standard 4-color G plus per-product icons (Gmail, Drive).
import googleLogo from "@adva/brand-assets/integrations/google.svg";
import housecallProLogo from "@adva/brand-assets/integrations/housecall-pro.svg";
import hubspotLogo from "@adva/brand-assets/integrations/hubspot.svg";
import jobberLogo from "@adva/brand-assets/integrations/jobber.svg";
import microsoft365Logo from "@adva/brand-assets/integrations/microsoft-365.svg";
import postmarkLogo from "@adva/brand-assets/integrations/postmark.svg";
import quickbooksLogo from "@adva/brand-assets/integrations/quickbooks.svg";
import salesaskLogo from "@adva/brand-assets/integrations/salesask.svg";
import serviceFusionLogo from "@adva/brand-assets/integrations/service-fusion.svg";
import servicetitanLogo from "@adva/brand-assets/integrations/servicetitan.svg";
import stripeLogo from "@adva/brand-assets/integrations/stripe.svg";
import thumbtackLogo from "@adva/brand-assets/integrations/thumbtack.svg";
import twilioLogo from "@adva/brand-assets/integrations/twilio.svg";
import xeroLogo from "@adva/brand-assets/integrations/xero.svg";

const REQUEST_ACCESS_EMAIL = "ryan@getadva.ai";

interface IntegrationDef {
  source: string;
  name: string;
  description: string;
  category: string;
  available: boolean;
  // Optional: missing logos render an initial-tile fallback.
  logoSrc?: string;
}

// Display order for category sections. Categories listed here render first
// in this order; any new category not listed falls through to the end.
const CATEGORY_ORDER = [
  "Field Service",
  "CRM",
  "Payments",
  "Accounting",
  "Lead Generation",
  "Communications",
  "Photo & Media",
  "Productivity",
  "Reviews",
  "Sales",
  "Other",
] as const;

const INTEGRATIONS: IntegrationDef[] = [
  {
    source: "jobber",
    name: "Jobber",
    description:
      "Import customers, jobs, and invoices from your Jobber account.",
    category: "Field Service",
    available: true,
    logoSrc: jobberLogo,
  },
  {
    source: "servicetitan",
    name: "ServiceTitan",
    description:
      "Enterprise field service for HVAC, plumbing, and electrical contractors.",
    category: "Field Service",
    available: false,
    logoSrc: servicetitanLogo,
  },
  {
    source: "housecall-pro",
    name: "Housecall Pro",
    description:
      "Field service management with deep event coverage for jobs, estimates, and invoices.",
    category: "Field Service",
    available: false,
    logoSrc: housecallProLogo,
  },
  {
    source: "service-fusion",
    name: "Service Fusion",
    description:
      "Field service management for small and mid-sized contractors.",
    category: "Field Service",
    available: false,
    logoSrc: serviceFusionLogo,
  },
  {
    source: "gohighlevel",
    name: "HighLevel",
    description: "Sync leads and automate marketing workflows with HighLevel.",
    category: "CRM",
    available: false,
    logoSrc: gohighlevelLogo,
  },
  {
    source: "hubspot",
    name: "HubSpot",
    description: "CRM and marketing automation for inbound sales pipelines.",
    category: "CRM",
    available: false,
    logoSrc: hubspotLogo,
  },
  {
    source: "stripe",
    name: "Stripe",
    description:
      "Accept payments and sync transactions via Adva's Stripe Connect platform.",
    category: "Payments",
    available: false,
    logoSrc: stripeLogo,
  },
  {
    source: "quickbooks",
    name: "QuickBooks Online",
    description:
      "Sync customers, invoices, and payments with QuickBooks Online.",
    category: "Accounting",
    available: false,
    logoSrc: quickbooksLogo,
  },
  {
    source: "xero",
    name: "Xero",
    description: "Sync financial data with Xero for accurate bookkeeping.",
    category: "Accounting",
    available: false,
    logoSrc: xeroLogo,
  },
  {
    source: "angi-leads",
    name: "Angi Leads",
    description:
      "Pay-per-lead from Angi (formerly HomeAdvisor) for service pros.",
    category: "Lead Generation",
    available: false,
    logoSrc: angiLogo,
  },
  {
    source: "angi-ads",
    name: "Angi Ads",
    description: "Display advertising leads from Angi's ads network.",
    category: "Lead Generation",
    available: false,
    logoSrc: angiLogo,
  },
  {
    source: "thumbtack",
    name: "Thumbtack",
    description: "Local services marketplace leads from Thumbtack.",
    category: "Lead Generation",
    available: false,
    logoSrc: thumbtackLogo,
  },
  {
    source: "google-lsa",
    name: "Google Local Services Ads",
    description:
      "Exclusive Google-verified leads from Local Services Ads campaigns.",
    category: "Lead Generation",
    available: false,
    logoSrc: googleLogo,
  },
  {
    source: "callrail",
    name: "CallRail",
    description: "Call tracking and form attribution for marketing campaigns.",
    category: "Lead Generation",
    available: false,
    logoSrc: callrailLogo,
  },
  {
    source: "twilio",
    name: "Twilio",
    description:
      "SMS and voice messaging powered by Adva-managed Twilio subaccounts.",
    category: "Communications",
    available: false,
    logoSrc: twilioLogo,
  },
  {
    source: "postmark",
    name: "Postmark",
    description:
      "Transactional email delivery and engagement tracking via Postmark.",
    category: "Communications",
    available: false,
    logoSrc: postmarkLogo,
  },
  {
    source: "companycam",
    name: "CompanyCam",
    description: "Attach job site photos directly to work orders and invoices.",
    category: "Photo & Media",
    available: false,
    logoSrc: companycamLogo,
  },
  {
    source: "microsoft365",
    name: "Microsoft 365",
    description: "Connect Outlook calendar, email, and OneDrive.",
    category: "Productivity",
    available: false,
    logoSrc: microsoft365Logo,
  },
  {
    source: "google_workspace",
    name: "Google Workspace",
    description:
      "Connect your Workspace at the admin level so all users get calendar, mail, and contacts sync.",
    category: "Productivity",
    available: false,
    logoSrc: googleLogo,
  },
  {
    source: "google_business",
    name: "Google Business Profile",
    description: "Manage reviews and update your business listing on Google.",
    category: "Reviews",
    available: false,
    logoSrc: googleLogo,
  },
  {
    source: "salesask",
    name: "SalesAsk",
    description:
      "AI voice analytics for field sales calls — API access via partnership only.",
    category: "Sales",
    available: false,
    logoSrc: salesaskLogo,
  },
  {
    source: "request-other",
    name: "Suggest an integration",
    description:
      "Need a partner that's not listed? Tell us which one and we'll prioritize it.",
    category: "Other",
    available: false,
  },
];

function initialFor(name: string): string {
  return (
    name
      .replace(/[^A-Za-z0-9]/g, "")
      .slice(0, 2)
      .toUpperCase() || "?"
  );
}

function formatLastSync(iso: string | null): string {
  if (!iso) return "Never synced";
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  return `${diffDays}d ago`;
}

function IntegrationCard({
  def,
  connection,
  onRequestDisconnect,
}: {
  def: IntegrationDef;
  connection: IntegrationConnection | undefined;
  onRequestDisconnect: (source: string) => void;
}) {
  const connectMutation = useConnectIntegration();
  const syncMutation = useSyncIntegration();

  const isConnected = !!connection;
  const status = connection?.status;
  const needsReauth = status === "reauth_required" || status === "error";

  function handleConnect() {
    connectMutation.mutate(def.source, {
      onSuccess: (data: { auth_url: string }) => {
        window.location.href = data.auth_url;
      },
    });
  }

  function handleSync() {
    syncMutation.mutate(def.source);
  }

  return (
    <Card>
      <CardContent className="flex flex-col gap-4 p-5">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg border border-border bg-white flex items-center justify-center flex-shrink-0 overflow-hidden">
            {def.logoSrc ? (
              <img
                src={def.logoSrc}
                alt={def.name}
                className="h-7 w-7 object-contain"
              />
            ) : (
              <span
                aria-hidden="true"
                className="text-[11px] font-semibold tracking-tight text-[color:var(--color-text-md)]"
              >
                {initialFor(def.name)}
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-sm font-medium text-[color:var(--color-text-hi)]">
                {def.name}
              </span>
              {isConnected && status ? (
                <StatusBadge status={status} />
              ) : !def.available ? (
                <Badge
                  variant="secondary"
                  className="text-xs whitespace-nowrap"
                >
                  Coming Soon
                </Badge>
              ) : null}
            </div>
            <p className="text-xs text-[color:var(--color-text-md)] mt-0.5">
              {def.description}
            </p>
          </div>
        </div>

        {isConnected && connection && (
          <div className="text-xs text-[color:var(--color-text-lo)] border-t border-[color:var(--color-border)] pt-3 flex items-center justify-between">
            <span>Last sync: {formatLastSync(connection.last_sync_at)}</span>
            {connection.external_account_id && (
              <span className="font-mono">
                {connection.external_account_id}
              </span>
            )}
          </div>
        )}

        {needsReauth && (
          <div className="flex items-center gap-1.5 text-xs text-[color:var(--color-warning)]">
            <AlertCircle className="h-3 w-3 flex-shrink-0" />
            <span>Reconnection required to resume syncing.</span>
          </div>
        )}

        <div className="flex items-center gap-2 mt-auto">
          {!isConnected && def.available && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleConnect}
              disabled={connectMutation.isPending}
            >
              {connectMutation.isPending ? (
                <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
              ) : (
                <Plug className="mr-1.5 h-3 w-3" />
              )}
              Connect
            </Button>
          )}

          {!isConnected && !def.available && (
            <Button variant="outline" size="sm" asChild>
              <a
                href={`mailto:${REQUEST_ACCESS_EMAIL}?subject=${encodeURIComponent(
                  `Request early access: ${def.name}`,
                )}`}
              >
                Request early access
              </a>
            </Button>
          )}

          {isConnected && needsReauth && (
            <Button
              variant="outline"
              size="sm"
              className="border-[color:var(--color-warning)] text-[color:var(--color-warning)] hover:bg-[color:var(--color-warning)]/10"
              onClick={handleConnect}
              disabled={connectMutation.isPending}
            >
              {connectMutation.isPending ? (
                <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
              ) : (
                <Plug className="mr-1.5 h-3 w-3" />
              )}
              Reconnect
            </Button>
          )}

          {isConnected && !needsReauth && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleSync}
              disabled={syncMutation.isPending}
            >
              {syncMutation.isPending ? (
                <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
              ) : (
                <RefreshCw className="mr-1.5 h-3 w-3" />
              )}
              Sync now
            </Button>
          )}

          {isConnected && (
            <Button
              variant="outline"
              size="sm"
              className="text-[color:var(--color-text-md)] hover:text-destructive hover:border-destructive"
              onClick={() => onRequestDisconnect(def.source)}
              disabled={false}
            >
              <Unplug className="mr-1.5 h-3 w-3" />
              Disconnect
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function IntegrationsPage() {
  const { data: connections, isLoading, error } = useIntegrations();
  const disconnectMutation = useDisconnectIntegration();
  const [disconnectingSource, setDisconnectingSource] = useState<string | null>(
    null,
  );

  const bySource = new Map<string, IntegrationConnection>(
    (connections ?? []).map((c) => [c.source, c]),
  );

  const presentCategories = [...new Set(INTEGRATIONS.map((i) => i.category))];
  const categories = [
    ...CATEGORY_ORDER.filter((c) => presentCategories.includes(c)),
    ...presentCategories.filter(
      (c) => !(CATEGORY_ORDER as readonly string[]).includes(c),
    ),
  ];

  const disconnectingName =
    INTEGRATIONS.find((i) => i.source === disconnectingSource)?.name ??
    disconnectingSource;

  return (
    <SettingsPage
      title="Integrations"
      subtitle="Connect third-party services to sync data with your business."
    >
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load integration status. Please refresh.
          </AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-5 w-5 animate-spin text-[color:var(--color-text-lo)]" />
        </div>
      ) : (
        <div className="space-y-8">
          {categories.map((category) => {
            const items = INTEGRATIONS.filter((i) => i.category === category);
            return (
              <section key={category}>
                <h3 className="typo-label uppercase tracking-wide text-[color:var(--color-text-lo)] mb-3">
                  {category}
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  {items.map((def) => (
                    <IntegrationCard
                      key={def.source}
                      def={def}
                      connection={bySource.get(def.source)}
                      onRequestDisconnect={setDisconnectingSource}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        open={!!disconnectingSource}
        onOpenChange={(open) => !open && setDisconnectingSource(null)}
        title={`Disconnect ${disconnectingName}?`}
        description="This will stop all syncs for this integration."
        confirmLabel="Disconnect"
        onConfirm={() => {
          if (disconnectingSource) {
            disconnectMutation.mutate(disconnectingSource);
            setDisconnectingSource(null);
          }
        }}
      />
    </SettingsPage>
  );
}
